import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import ServiceToggle from './ServiceToggle';
import { 
  Clock, 
  MapPin, 
  Play, 
  Square, 
  Settings,
  Loader2,
  Download,
  Plus,
  Trash2,
  Users,
  Edit2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
} from './ui/select';
import { MapContainer, TileLayer, Circle, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Map recenter component
const MapRecenter = ({ lat, lng }) => {
  const map = useMap();
  useEffect(() => {
    if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
      map.setView([lat, lng], 15);
    }
  }, [lat, lng, map]);
  return null;
};

// Mini map preview component for work site
const WorkSiteMapPreview = ({ lat, lng, radiusMeters }) => {
  const parsedLat = parseFloat(lat);
  const parsedLng = parseFloat(lng);
  const radius = parseFloat(radiusMeters) || 200;
  
  if (isNaN(parsedLat) || isNaN(parsedLng)) return null;
  
  return (
    <MapContainer
      center={[parsedLat, parsedLng]}
      zoom={15}
      style={{ height: '200px', width: '100%' }}
      zoomControl={false}
      dragging={false}
      scrollWheelZoom={false}
      doubleClickZoom={false}
      attributionControl={false}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <MapRecenter lat={parsedLat} lng={parsedLng} />
      <Marker position={[parsedLat, parsedLng]} />
      <Circle
        center={[parsedLat, parsedLng]}
        radius={radius}
        pathOptions={{
          color: '#2563EB',
          fillColor: '#3B82F6',
          fillOpacity: 0.2,
          weight: 2,
          dashArray: '5, 5'
        }}
      />
    </MapContainer>
  );
};

const TimeclockTab = () => {
  // Core state
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clocking, setClocking] = useState(false);
  const [settings, setSettings] = useState(null);
  const [workSites, setWorkSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [sessionExpired, setSessionExpired] = useState(false);
  
  // Time records
  const [timeRecords, setTimeRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [filterMember, setFilterMember] = useState('all');
  const [filterDate, setFilterDate] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const recordsPerPage = 20;
  
  // Settings modal
  const [showSettings, setShowSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Work site modal (simplified - no zones)
  const [showSiteModal, setShowSiteModal] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [siteForm, setSiteForm] = useState({ 
    name: '', 
    lat: '', 
    lng: '', 
    radiusMeters: 200,
    timezone: 'America/Chicago' 
  });
  const [savingSite, setSavingSite] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadTimeRecords();
  }, [filterMember, filterDate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setSessionExpired(false);
      const [statusRes, settingsRes, sitesRes, teamRes] = await Promise.all([
        api.get('/timeclock/status'),
        api.get('/timeclock/settings'),
        api.get('/timeclock/work-sites'),
        api.get('/team').catch(() => ({ data: [] }))
      ]);
      
      // Check if auto clock-out occurred
      if (statusRes.data.autoClockOut) {
        toast.info(`You were automatically clocked out after ${statusRes.data.maxShiftHours || 14} hours`, {
          duration: 5000
        });
      }
      
      setStatus(statusRes.data);
      setSettings(settingsRes.data);
      setSettingsForm(settingsRes.data);
      setWorkSites(sitesRes.data.sites || []);
      setTeamMembers(teamRes.data || []);
      
      if (sitesRes.data.sites?.length === 1) {
        setSelectedSiteId(sitesRes.data.sites[0].id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      // Handle 401 gracefully - don't redirect, show session expired message
      if (error.response?.status === 401) {
        setSessionExpired(true);
        toast.error('Your session has expired. Please refresh or log in again.', {
          duration: 10000
        });
      } else {
        toast.error('Failed to load timeclock');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTimeRecords = async () => {
    try {
      setRecordsLoading(true);
      const now = new Date();
      let start;
      
      if (filterDate === 'today') {
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      } else if (filterDate === 'week') {
        start = new Date(now);
        start.setDate(start.getDate() - 7);
      } else if (filterDate === 'month') {
        start = new Date(now.getFullYear(), now.getMonth(), 1);
      } else if (filterDate === 'year') {
        start = new Date(now.getFullYear(), 0, 1);
      } else {
        // Default to month
        start = new Date(now.getFullYear(), now.getMonth(), 1);
      }
      
      const params = new URLSearchParams({
        start: start.toISOString(),
        end: now.toISOString(),
        limit: '1000',
        sort: 'desc' // Latest first
      });
      
      if (filterMember !== 'all') {
        params.append('employee_id', filterMember);
      }
      
      const response = await api.get(`/timeclock/shifts?${params}`);
      const shifts = response.data.shifts || [];
      
      // Sort by clock-in time, latest first (descending)
      shifts.sort((a, b) => {
        const dateA = new Date(a.clockInTimeUTC || 0);
        const dateB = new Date(b.clockInTimeUTC || 0);
        return dateB - dateA; // Descending
      });
      
      setTimeRecords(shifts);
      setTotalRecords(shifts.length);
      setCurrentPage(1); // Reset to first page on filter change
    } catch (error) {
      console.error('Error loading records:', error);
    } finally {
      setRecordsLoading(false);
    }
  };

  const getLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracyMeters: pos.coords.accuracy
        }),
        (err) => {
          const msg = err.code === 1 ? 'Location permission denied' :
                      err.code === 2 ? 'Location unavailable' : 'Location timeout';
          reject(new Error(msg));
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  }, []);

  const handleClockIn = async () => {
    if (!selectedSiteId) {
      toast.error('Please select a work site');
      return;
    }
    
    try {
      setClocking(true);
      let location = null;
      
      if (settings?.geoGateClockInEnabled !== false) {
        try {
          location = await getLocation();
        } catch (error) {
          toast.error(error.message);
          return;
        }
      }
      
      await api.post('/timeclock/clock-in', {
        siteId: selectedSiteId,
        lat: location?.lat,
        lng: location?.lng,
        accuracyMeters: location?.accuracyMeters,
        source: 'web',
        idempotencyKey: `in-${Date.now()}`
      });
      
      toast.success('Clocked in! ✓');
      loadData();
      loadTimeRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    } finally {
      setClocking(false);
    }
  };

  const handleClockOut = async () => {
    try {
      setClocking(true);
      let location = null;
      
      if (settings?.geoGateClockOutEnabled) {
        try {
          location = await getLocation();
        } catch (error) {
          toast.error(error.message);
          return;
        }
      }
      
      const res = await api.post('/timeclock/clock-out', {
        lat: location?.lat,
        lng: location?.lng,
        accuracyMeters: location?.accuracyMeters,
        source: 'web',
        idempotencyKey: `out-${Date.now()}`
      });
      
      toast.success(`Clocked out! ${res.data.hoursWorked}h logged`);
      loadData();
      loadTimeRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock out');
    } finally {
      setClocking(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      await api.put('/timeclock/settings', settingsForm);
      setSettings(settingsForm);
      toast.success('Settings saved');
      setShowSettings(false);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleAddSite = () => {
    setEditingSite(null);
    setSiteForm({ name: '', lat: '', lng: '', radiusMeters: 200, timezone: 'America/Chicago' });
    setShowSiteModal(true);
  };

  const handleEditSite = (site) => {
    setEditingSite(site);
    // Extract lat/lng from first geofence if exists
    const gf = site.geofences?.[0];
    setSiteForm({
      name: site.name,
      lat: gf?.lat?.toString() || '',
      lng: gf?.lng?.toString() || '',
      radiusMeters: gf?.radiusMeters || 200,
      timezone: site.timezone || 'America/Chicago'
    });
    setShowSiteModal(true);
  };

  const handleSaveSite = async () => {
    if (!siteForm.name.trim()) {
      toast.error('Site name is required');
      return;
    }
    if (!siteForm.lat || !siteForm.lng) {
      toast.error('Location is required');
      return;
    }
    
    const radius = parseFloat(siteForm.radiusMeters) || 200;
    if (radius < 50) {
      toast.error('Radius must be at least 50 meters for accurate GPS tracking');
      return;
    }
    
    try {
      setSavingSite(true);
      
      // Convert simple form to API format (single geofence)
      const payload = {
        name: siteForm.name,
        timezone: siteForm.timezone,
        geofences: [{
          id: editingSite?.geofences?.[0]?.id || `gf-${Date.now()}`,
          name: siteForm.name,
          type: 'CIRCLE',
          lat: parseFloat(siteForm.lat),
          lng: parseFloat(siteForm.lng),
          radiusMeters: radius
        }]
      };
      
      if (editingSite) {
        await api.put(`/timeclock/work-sites/${editingSite.id}`, payload);
        toast.success('Work site updated');
      } else {
        await api.post('/timeclock/work-sites', payload);
        toast.success('Work site created');
      }
      
      setShowSiteModal(false);
      loadData();
    } catch (error) {
      toast.error('Failed to save work site');
    } finally {
      setSavingSite(false);
    }
  };

  const handleDeleteSite = async (site) => {
    if (!confirm(`Delete "${site.name}"?`)) return;
    try {
      await api.delete(`/timeclock/work-sites/${site.id}`);
      toast.success('Deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleUseMyLocation = async () => {
    try {
      setGettingLocation(true);
      const loc = await getLocation();
      setSiteForm(prev => ({
        ...prev,
        lat: loc.lat.toFixed(6),
        lng: loc.lng.toFixed(6)
      }));
      toast.success('Location set');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setGettingLocation(false);
    }
  };

  // Export-specific formatters (clean, no timezone suffix for cleaner CSV)
  const formatTimeForExport = (iso) => {
    if (!iso) return '-';
    const date = new Date(iso);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit'
    });
  };

  const formatDateForExport = (iso) => {
    if (!iso) return '-';
    const date = new Date(iso);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleExport = () => {
    // Get timezone abbreviation once for header
    const tzAbbr = new Date().toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop();
    
    // Sort records by date, latest first (already sorted, but ensure)
    const sortedRecords = [...timeRecords].sort((a, b) => {
      const dateA = new Date(a.clockInTimeUTC || 0);
      const dateB = new Date(b.clockInTimeUTC || 0);
      return dateB - dateA; // Descending - latest first
    });
    
    const rows = [
      ['Name', 'Date', `Clock In (${tzAbbr})`, `Clock Out (${tzAbbr})`, 'Hours Worked', 'Site'],
      ...sortedRecords.map(r => {
        // Calculate hours properly
        let hoursWorked = '-';
        if (r.status === 'CLOSED' && r.totalHours !== null && r.totalHours !== undefined) {
          hoursWorked = r.totalHours.toFixed(2);
        } else if (r.status === 'OPEN') {
          hoursWorked = 'In Progress';
        }
        
        return [
          r.employeeName || 'Unknown',
          formatDateForExport(r.clockInTimeUTC),
          formatTimeForExport(r.clockInTimeUTC),
          r.status === 'OPEN' ? 'Still Working' : formatTimeForExport(r.closedAt),
          hoursWorked,
          r.siteName || '-'
        ];
      })
    ];
    
    // Escape CSV values that contain commas
    const escapeCsv = (val) => {
      if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    };
    
    const csv = rows.map(r => r.map(escapeCsv).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `timeclock-${filterDate}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success(`Exported ${sortedRecords.length} records`);
  };

  // Display formatters (with timezone for UI clarity)
  const formatTime = (iso) => {
    if (!iso) return '-';
    const date = new Date(iso);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    const date = new Date(iso);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric'
    });
  };

  const formatDuration = (start) => {
    if (!start) return '-';
    // Parse the UTC timestamp properly
    const startDate = new Date(start);
    const now = new Date();
    const ms = now.getTime() - startDate.getTime();
    
    // Handle edge case of negative duration (shouldn't happen but safeguard)
    if (ms < 0) {
      return '0h 0m';
    }
    
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `${h}h ${m}m`;
  };

  const totalHours = timeRecords
    .filter(r => r.status === 'CLOSED')
    .reduce((sum, r) => sum + (r.totalHours || 0), 0);

  const isClockedIn = status?.isClockedIn;
  const openShift = status?.openShift;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Session Expired Banner */}
      {sessionExpired && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="font-medium text-amber-800">Session Expired</p>
              <p className="text-sm text-amber-600">Your session has timed out. Please refresh the page to continue.</p>
            </div>
          </div>
          <Button 
            onClick={() => window.location.reload()} 
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-100"
          >
            Refresh Page
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Timeclock</h2>
          <p className="text-gray-600">Clock in and track hours</p>
        </div>
        <Button variant="outline" onClick={() => setShowSettings(true)}>
          <Settings className="w-4 h-4 mr-2" />
          Settings
        </Button>
      </div>

      {/* Clock In/Out */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            {/* Work Site Select */}
            <div className="flex-1">
              <Label className="text-sm text-gray-600 mb-2 block">Work Site</Label>
              <Select 
                value={selectedSiteId} 
                onValueChange={(val) => {
                  if (val === '__add_site__') {
                    handleAddSite();
                  } else {
                    setSelectedSiteId(val);
                  }
                }} 
                disabled={isClockedIn}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select work site..." />
                </SelectTrigger>
                <SelectContent>
                  {workSites.map(site => (
                    <SelectItem key={site.id} value={site.id}>
                      <span className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        {site.name}
                      </span>
                    </SelectItem>
                  ))}
                  {workSites.length > 0 && <SelectSeparator />}
                  <SelectItem value="__add_site__" className="text-blue-600">
                    <span className="flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      Add Site
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              {workSites.length === 0 && !loading && (
                <p className="text-sm text-amber-600 mt-1">
                  No work sites configured yet.
                </p>
              )}
            </div>

            {/* Status & Button */}
            <div className="flex flex-col items-center gap-3">
              {/* Only show badge when clocked in */}
              {isClockedIn && (
                <Badge variant="default" className="px-3 py-1">
                  <Clock className="w-4 h-4 mr-1" />
                  Working • {formatDuration(openShift?.clockInTimeUTC)}
                </Badge>
              )}

              {!isClockedIn ? (
                <Button
                  size="lg"
                  className="h-12 px-8 bg-green-600 hover:bg-green-700"
                  onClick={handleClockIn}
                  disabled={clocking || !selectedSiteId}
                >
                  {clocking ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                    <><Play className="w-5 h-5 mr-2" />Clock In</>
                  )}
                </Button>
              ) : (
                <Button
                  size="lg"
                  variant="destructive"
                  className="h-12 px-8"
                  onClick={handleClockOut}
                  disabled={clocking}
                >
                  {clocking ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                    <><Square className="w-5 h-5 mr-2" />Clock Out</>
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Time Records */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5" />
              Time Records
            </CardTitle>
            
            <div className="flex items-center gap-2">
              <Select value={filterMember} onValueChange={setFilterMember}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Team</SelectItem>
                  {teamMembers.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterDate} onValueChange={setFilterDate}>
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">Week</SelectItem>
                  <SelectItem value="month">Month</SelectItem>
                  <SelectItem value="year">Year</SelectItem>
                </SelectContent>
              </Select>

              <Button variant="outline" size="icon" onClick={handleExport} title="Export CSV">
                <Download className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {recordsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : timeRecords.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No records found</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-500">
                      <th className="pb-2 font-medium">Name</th>
                      <th className="pb-2 font-medium">Date</th>
                      <th className="pb-2 font-medium">In</th>
                      <th className="pb-2 font-medium">Out</th>
                      <th className="pb-2 font-medium">Site</th>
                      <th className="pb-2 font-medium text-right">Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timeRecords
                      .slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage)
                      .map(r => (
                      <tr key={r.id} className="border-b last:border-0">
                        <td className="py-3 font-medium">{r.employeeName || 'Unknown'}</td>
                        <td className="py-3 text-gray-600">{formatDate(r.clockInTimeUTC)}</td>
                        <td className="py-3">{formatTime(r.clockInTimeUTC)}</td>
                        <td className="py-3">
                          {r.status === 'OPEN' 
                            ? <Badge variant="outline" className="text-green-600">Working</Badge>
                            : formatTime(r.closedAt)
                          }
                        </td>
                        <td className="py-3 text-gray-600">{r.siteName || '-'}</td>
                        <td className="py-3 text-right font-medium">
                          {r.status === 'OPEN' 
                            ? <span className="text-green-600">{formatDuration(r.clockInTimeUTC)}</span>
                            : `${r.totalHours?.toFixed(2) || '-'}h`
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm">
                <div className="flex items-center gap-4">
                  <span className="text-gray-600">
                    Showing {Math.min((currentPage - 1) * recordsPerPage + 1, totalRecords)}-{Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords} records
                  </span>
                  <span className="font-semibold">Total: {totalHours.toFixed(2)}h</span>
                </div>
                
                {totalRecords > recordsPerPage && (
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="px-2 text-gray-600">
                      Page {currentPage} of {Math.ceil(totalRecords / recordsPerPage)}
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setCurrentPage(p => Math.min(Math.ceil(totalRecords / recordsPerPage), p + 1))}
                      disabled={currentPage >= Math.ceil(totalRecords / recordsPerPage)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Settings Modal */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Timeclock Settings</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-5 py-4">
            {/* Location Settings */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Enforce geofence for clock IN</Label>
                  <p className="text-xs text-gray-500">Must be at work site to clock in</p>
                </div>
                <ServiceToggle
                  checked={settingsForm.geoGateClockInEnabled ?? true}
                  onChange={(v) => setSettingsForm(p => ({ ...p, geoGateClockInEnabled: v }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Enforce geofence for clock OUT</Label>
                  <p className="text-xs text-gray-500">Must be at work site to clock out</p>
                </div>
                <ServiceToggle
                  checked={settingsForm.geoGateClockOutEnabled ?? false}
                  onChange={(v) => setSettingsForm(p => ({ ...p, geoGateClockOutEnabled: v }))}
                />
              </div>
              <div className="flex items-center gap-3">
                <Label className="whitespace-nowrap">GPS accuracy</Label>
                <Input
                  type="number"
                  value={settingsForm.minAccuracyMeters || 75}
                  onChange={(e) => setSettingsForm(p => ({ ...p, minAccuracyMeters: parseFloat(e.target.value) }))}
                  className="w-20"
                />
                <span className="text-sm text-gray-500">meters</span>
              </div>
              <div className="flex items-center gap-3">
                <Label className="whitespace-nowrap">Auto clock-out</Label>
                <Input
                  type="number"
                  value={settingsForm.maxShiftHours || 14}
                  onChange={(e) => setSettingsForm(p => ({ ...p, maxShiftHours: parseFloat(e.target.value) }))}
                  className="w-20"
                />
                <span className="text-sm text-gray-500">hours</span>
              </div>
            </div>

            {/* Work Sites */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label>Work Sites</Label>
                <Button size="sm" variant="outline" onClick={handleAddSite}>
                  <Plus className="w-4 h-4 mr-1" />Add
                </Button>
              </div>
              
              {workSites.length === 0 ? (
                <p className="text-sm text-gray-500">No work sites</p>
              ) : (
                <div className="space-y-2">
                  {workSites.map(site => (
                    <div key={site.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div>
                        <p className="font-medium text-sm">{site.name}</p>
                        <p className="text-xs text-gray-500">
                          {site.geofences?.[0]?.radiusMeters || 200}m radius
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleEditSite(site)}>
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteSite(site)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
            <Button onClick={handleSaveSettings} disabled={savingSettings}>
              {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Work Site Modal (Simplified) */}
      <Dialog open={showSiteModal} onOpenChange={setShowSiteModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editingSite ? 'Edit Work Site' : 'Add Work Site'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Name</Label>
              <Input
                value={siteForm.name}
                onChange={(e) => setSiteForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Main Marina"
              />
            </div>
            
            <div>
              <Label>Location</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Input
                  placeholder="Latitude"
                  value={siteForm.lat}
                  onChange={(e) => setSiteForm(p => ({ ...p, lat: e.target.value }))}
                />
                <Input
                  placeholder="Longitude"
                  value={siteForm.lng}
                  onChange={(e) => setSiteForm(p => ({ ...p, lng: e.target.value }))}
                />
              </div>
              <Button 
                size="sm" 
                variant="outline" 
                className="mt-2 w-full"
                onClick={handleUseMyLocation}
                disabled={gettingLocation}
              >
                {gettingLocation ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : (
                  <MapPin className="w-4 h-4 mr-1" />
                )}
                Use My Current Location
              </Button>
            </div>

            <div>
              <Label>Radius (meters)</Label>
              <Input
                type="number"
                value={siteForm.radiusMeters}
                onChange={(e) => setSiteForm(p => ({ ...p, radiusMeters: e.target.value }))}
                placeholder="200"
                min="50"
              />
              <p className="text-xs text-gray-500 mt-1">How close employees must be to clock in (min 50m)</p>
            </div>

            {/* Map Preview - Interactive Leaflet Map */}
            {siteForm.lat && siteForm.lng && !isNaN(parseFloat(siteForm.lat)) && !isNaN(parseFloat(siteForm.lng)) && (
              <div>
                <Label>Location Preview</Label>
                <div className="mt-1 rounded-lg overflow-hidden border relative" style={{ height: '200px' }}>
                  <WorkSiteMapPreview 
                    lat={siteForm.lat} 
                    lng={siteForm.lng} 
                    radiusMeters={siteForm.radiusMeters}
                  />
                  {/* Radius overlay indicator */}
                  <div className="absolute bottom-2 left-2 bg-white/90 px-2 py-1 rounded text-xs font-medium z-[1000]">
                    📍 {parseFloat(siteForm.radiusMeters) || 200}m radius
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">Blue circle shows the geofence area</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSiteModal(false)}>Cancel</Button>
            <Button onClick={handleSaveSite} disabled={savingSite}>
              {savingSite ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeclockTab;
